<template>
  <div class="wishlist">
    <ui-title :title="t('wishlist.title')" :wave="'/wishlistTitleBg.png'" />
    <div class="wishlist__block">
      <div class="wishlist__bg"></div>
      <div class="container">
      <div class="wishlist__wrapper">
        <div class="wishlist__main">
          <div class="wishlist__main-top">
            <p>{{ t('cart.product') }}</p>
            <p>{{ t('cart.price') }}</p>
          </div>
          <div class="wishlist__main-list">
            <div class="wishlist__main-product" v-for="product in productsInWishlist" :key="product.node.uuid">
              <div>
                <svg @click="removeFromWishlist(product.node.uuid)" width="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23.7998 6.96898C20.0838 6.60072 16.3454 6.41101 12.6181 6.41101C10.4085 6.41101 8.19898 6.52261 5.98941 6.74579L3.71289 6.96898" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.85107 5.84175L10.0966 4.37987C10.2751 3.31972 10.409 2.5274 12.295 2.5274H15.2188C17.1047 2.5274 17.2498 3.36436 17.4172 4.39103L17.6627 5.84175" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M17.3387 24.8467H10.1743C7.06086 24.8467 6.96042 23.4852 6.83767 21.7332L6.1123 10.4957" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21.4011 10.4957L20.6758 21.7332" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.8926 18.7087H15.6087" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14.6714 14.2448H16.5462" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10.9663 14.2448H11.8925" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <router-link :to="translations.i18nRoute({ name: 'product', params: { productUuid: product.node.uuid } })">
                  <img class="wishlist__main-product-image" :src="product.node.images.edges[0].node.image" :alt="product.node.name">
                </router-link>
                <h6>{{ product.node.name }}</h6>
              </div>
              <p><img src="@icons/currency.svg" alt="currency"> {{ product.node.price }}</p>
            </div>
          </div>
          <div class="wishlist__main-checkout" v-if="productsInWishlist.length">
            <div>
              <p>{{ t('cart.total') }} <span><img src="@icons/currency.svg" alt="currency">{{ totalPrice }}</span></p>
            </div>
            <ui-button
                @click="addAllToCart"
                class="wishlist__main-checkout-button"
            >
              {{ t('buttons.addToCart') }}
              <svg width="25" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7.74365 7.66964V6.69964C7.74365 4.44964 9.55365 2.23964 11.8037 2.02964C14.4837 1.76964 16.7437 3.87964 16.7437 6.50964V7.88964"
                    stroke="white" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M9.24348 21.9998H15.2435C19.2635 21.9998 19.9835 20.3898 20.1935 18.4298L20.9435 12.4298C21.2135 9.98976 20.5135 7.99976 16.2435 7.99976H8.24348C3.97348 7.99976 3.27348 9.98976 3.54348 12.4298L4.29348 18.4298C4.50348 20.3898 5.22348 21.9998 9.24348 21.9998Z"
                    stroke="white" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M15.7396 12H15.7486" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8.73816 12H8.74714" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </ui-button>
          </div>
          <p class="wishlist__main-empty" v-else>{{ t('wishlist.empty') }}</p>
        </div>
        <div class="wishlist__like">
          <p class="wishlist__like-title">{{ t('cart.alsoLike') }}</p>
          <swiper
              class="wishlist__like-swiper"
              :modules="[Pagination]"
              :pagination="{
                clickable: true,
              }"
              :loop="true"
              :speed="500"
              :breakpoints="{
                200: {
                  slidesPerView: 1,
                  spaceBetween: 50
                }
              }"
              v-if="alsoLikeProducts"
          >
            <swiper-slide
                v-for="(productPair, index) in products"
                :key="index"
            >
              <div class="wishlist__like-list">
                <product-card
                    v-for="product in productPair"
                    :key="product.node.uuid"
                    :product="product.node"
                />
              </div>
            </swiper-slide>
          </swiper>
          <div class="wishlist__like-list" v-if="isLoading">
            <product-card-skeleton
              v-for="idx in 2"
              :key="idx"
            />
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import UiTitle from "@/components/ui/ui-title.vue";
import {useI18n} from "vue-i18n";
import {computed, onMounted, ref, watch} from "vue";
import {useCartStore} from "@/stores/cart.js";
import translations from "@/core/helpers/translations.js";
import {Products} from "@/core/api/products.js";
import ProductCard from "@/components/cards/product-card.vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css/pagination';
import 'swiper/css';
import {Pagination} from "swiper/modules";
import UiButton from "@/components/ui/ui-button.vue";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import {useWishlistStore} from "@/stores/wishlist.js";
import {Wishlist} from "@/core/api/wishlist.js";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {Orders} from "@/core/api/orders.js";

const {t, locale} = useI18n()
const wishlistStore = useWishlistStore()
const cartStore = useCartStore()
const authStore = useAuthStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})

const totalPrice = computed(() => {
  if (!wishlistStore.wishlist?.products?.edges) return 0;

  return wishlistStore.wishlist.products.edges.reduce((sum, edge) => {
    const rawPrice = edge.node.price;

    const price = parseFloat(
        String(rawPrice).replace(',', '.').replace(/[^\d.]/g, '')
    ) || 0;

    return sum + price;
  }, 0);
});
const productsInWishlist = computed(() => {
  return wishlistStore.wishlist?.products ? wishlistStore.wishlist.products.edges : []
})

const alsoLikeProducts = ref([])
const isLoading = ref(false)
const products = computed(() => {
  if (!alsoLikeProducts.value.length) return [];

  const groupedProducts = [];
  for (let i = 0; i < alsoLikeProducts.value.length; i += 2) {
    const pair = [alsoLikeProducts.value[i]];
    if (alsoLikeProducts.value[i + 1]) {
      pair.push(alsoLikeProducts.value[i + 1]);
    }
    groupedProducts.push(pair);
  }
  return groupedProducts;
});

const removeFromWishlist = async (productUuid) => {
  const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, productUuid)
  wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
}

const addAllToCart = async () => {
  let addedCount = 0;

  for (const product of productsInWishlist.value) {
    const productUuid = product.node.uuid;

    const isInCart = cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === productUuid)

    if (!isInCart) {
      try {
        const res = await Orders.overwriteCart(
          "addOrderProduct",
          accessToken.value,
          orderUuid.value,
          productUuid
        );

        if (!res.errors) {
          addedCount++;
          cartStore.setCurrentOrders(res.data.addOrderProduct.order);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  if (addedCount > 0) {
    ElNotification({
      message: t('popup.allAddedToCart'),
      type: "success"
    });
  } else {
    ElNotification({
      message: t('popup.allAddedToCartFail'),
      type: "info"
    });
  }
};

const getProducts = async () => {
  isLoading.value = true

  const res = await Products.getProducts(`tags: "top"`)
  alsoLikeProducts.value = res.data.products.edges.slice(0, 6)

  isLoading.value = false
}

watch(
    () => locale.value,
    async () => {
      alsoLikeProducts.value = [];
      await getProducts()
    }
)

onMounted( async () => {
  await getProducts()
})
</script>

<style lang="scss" scoped>
.wishlist {
  top: -103px;
  background-color: $white;

  &__block {
    position: relative;
    padding-bottom: 50px;
  }

  &__bg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 600px;
    background-color: #F3FAFD;
  }

  &__wrapper {
    display: flex;
    align-items: flex-start;
    gap: 150px;
    padding-top: 55px;
  }

  &__main {
    width: 100%;

    &-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 2px solid #5B194F;
      padding-inline: 25px;
      padding-bottom: 10px;

      & p {
        color: #696969;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
      }
    }

    &-list {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    &-product {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 25px 15px;
      border-bottom: 1px solid #B1B1B1;

      & div {
        display: flex;
        align-items: center;
        gap: 25px;

        & svg {
          cursor: pointer;
        }
      }

      & h6 {
        color: #2B3044;
        font-family: Syne, sans-serif;
        font-size: 20px;
        font-weight: 700;
      }

      & p {
        display: flex;
        align-items: center;
        gap: 5px;

        color: #2B3044;
        font-size: 16px;
        font-weight: 500;

        & img {
          width: 30px;
        }
      }

      &-image {
        border-radius: 20px;
        width: 130px;
        height: 80px;
        object-fit: cover;
      }

      &:last-child {
        border-bottom: none;
      }
    }

    &-checkout {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-left: 25px;
      border-radius: 4px;
      background-color: #F3FAFD;

      & div {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      & p {
        display: flex;
        align-items: center;
        gap: 10px;

        color: #2B3044;
        text-align: center;
        font-size: 20px;
        font-weight: 500;

        & span {
          display: flex;
          align-items: center;
          gap: 5px;

          color: #2B3044;
          font-size: 16px;
          font-weight: 500;

          & img {
            width: 27px;
          }
        }
      }

      &-button {
        width: fit-content;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 10px 20px;
      }
    }

    &-empty {
      padding-top: 20px;
      text-align: center;
      color: #2B3044;
      font-family: Syne, sans-serif;
      font-size: 20px;
      font-weight: 700;
    }
  }

  &__like {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 30px;

    &-title {
      flex-shrink: 0;
      font-family: Syne, sans-serif;
      font-size: 20px;
      font-weight: 700;
      background: linear-gradient(90deg, #FF85FD 0.5%, #7D045A 44.83%, #3D2B44 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &-swiper {
      width: 400px;
      padding-inline: 10px;
      padding-bottom: 50px;
    }

    &-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    &-skeleton {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }
}

@media (max-width: 1680px) {
  .wishlist__bg {
    width: 700px;
  }
}

@media (max-width: 1550px) {
  .wishlist__bg {
    width: 600px;
  }
}

@media (max-width: 1300px) {
  .wishlist__bg {
    width: 500px;
  }
}

@media (max-width: 1100px) {
  .wishlist {
    &__bg {
      display: none;
    }

    &__wrapper {
      flex-direction: column;
    }

    &__like {
      width: 100%;
      padding-top: 25px;
      background-color: #F3FAFD;

      &-title {
        text-align: center;
      }
    }
  }
}

@media (max-width: 550px) {
  .wishlist {
    &__main {
      &-product {
        &-image {
          width: 110px;
          height: 70px;
        }

        & div {
          gap: 15px;
          flex-direction: column-reverse;
          align-items: flex-start;
        }

        & h6 {
          font-size: 16px;
        }
      }
    }
  }
}
</style>